import { useMemo } from 'react';
import styled from 'styled-components';
import type { FleetModel, FleetModelGroupRef, FleetStatus } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Combobox, type ComboboxOption } from '@shared/ui/Combobox';
import {
  BlockNote,
  FieldHint,
  FieldLabel,
  FleetChip,
  FleetStatusChip,
  TextButton,
  fleetLeading,
  fleetType,
  formatCount,
  formatIsoDate,
  formatMoney,
  typo,
} from '@entities/fleet';

/** Что показать о модели в составе: из справочника, а нет её там — из карточки группы. */
export interface ModelInfo {
  id: string;
  name: string;
  category: string | null;
  units: number;
  status: FleetStatus;
  /** ПРЦ на сегодня, ₽/маш.-ч без НДС. */
  prcPrice: string | null;
  /** С какой даты вступит утверждённая цена, если сегодня её ещё нет. */
  prcUpcomingFrom: string | null;
  /** В какой группе модель стоит сейчас (по справочнику). */
  group: FleetModelGroupRef | null;
}

function prcText(m: ModelInfo): string {
  if (m.prcPrice) return `ПРЦ ${formatMoney(m.prcPrice)} ₽`;
  if (m.prcUpcomingFrom) return typo(`ПРЦ вступит с ${formatIsoDate(m.prcUpcomingFrom)}`);
  return 'нет действующей ПРЦ';
}

const List = styled.ul`
  display: grid;
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  padding: 0;
  list-style: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  &:empty {
    display: none;
  }
`;

const Item = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  padding: ${({ theme }) => `${theme.spacing(2.5)} 0`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &[data-new='true'] {
    padding-left: ${({ theme }) => theme.spacing(3)};
    border-left: 3px solid ${({ theme }) => theme.colors.status.info};
  }

  &[data-moving='true'] {
    border-left-color: ${({ theme }) => theme.colors.status.warning};
  }
`;

const Name = styled.span`
  display: block;
  font-size: ${fleetType.base};
  line-height: ${fleetLeading.tight};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const Sub = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** «уйдёт из группы X» — словами и кромкой: читается и без цвета. */
const MovingNote = styled.span`
  display: block;
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: ${fleetType.small};
  font-weight: 500;
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.primary};
  text-wrap: pretty;
`;

const Removed = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};

  & > span {
    display: inline-flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(1.5)};
    margin-right: ${({ theme }) => theme.spacing(3)};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Empty = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: ${fleetType.body};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

interface ModelsEditorProps {
  /** Группа, которую правят; `null` — новая. */
  groupId: string | null;
  modelIds: readonly string[];
  /** Состав открытой версии — что добавлено и что убрано. */
  initialIds: readonly string[];
  info: (id: string) => ModelInfo | null;
  /** Модели справочника не в архиве — из них добавляют; у каждой её группа. */
  fleet: readonly FleetModel[];
  onChange: (ids: string[]) => void;
  domId: (key: string) => string;
}

/**
 * Состав группы: убрать модель, добавить из справочника, перенести из другой
 * группы. Модель стоит только в одной группе — добавленная из чужой уйдёт
 * оттуда при сохранении, это видно до сохранения.
 */
export function ModelsEditor({
  groupId,
  modelIds,
  initialIds,
  info,
  fleet,
  onChange,
  domId,
}: ModelsEditorProps) {
  const pickerId = domId('models-add');
  const before = useMemo(() => new Set(initialIds), [initialIds]);
  const inDraft = useMemo(() => new Set(modelIds), [modelIds]);
  const removed = initialIds.filter((id) => !inDraft.has(id));

  /** Группа, из которой модель уйдёт, если её добавить сюда. */
  const otherGroup = (id: string): string | null => {
    const g = info(id)?.group;
    return g && g.id !== groupId ? g.name : null;
  };

  // Варианты — один массив на состав: Combobox сбрасывает подсветку при новом массиве.
  const options = useMemo<ComboboxOption[]>(
    () =>
      fleet
        .filter((m) => !inDraft.has(m.id))
        .map((m) => {
          const place = !m.group
            ? 'вне групп'
            : m.group.id === groupId
              ? 'сейчас в этой группе'
              : `в группе «${m.group.name}»`;
          return {
            id: m.id,
            label: m.name,
            sublabel: [m.category_name, `${formatCount(m.units_count)} ед.`, place]
              .filter((s): s is string => Boolean(s))
              .join(' · '),
          };
        }),
    [fleet, inDraft, groupId],
  );

  const moving = modelIds.filter((id) => !before.has(id) && otherGroup(id) !== null).length;

  const removeId = (id: string) => domId(`model-remove-${id}`);

  /**
   * Кнопка, по которой щёлкнули, исчезает вместе со строкой — фокус переходит
   * на «Убрать» соседней строки (или вернувшейся модели), а без строк — на поиск.
   */
  const change = (ids: string[], focusModel: string | null) => {
    onChange(ids);
    requestAnimationFrame(() => {
      const target = focusModel ? document.getElementById(removeId(focusModel)) : null;
      (target ?? document.getElementById(pickerId))?.focus();
    });
  };

  const removeModel = (id: string) => {
    const i = modelIds.indexOf(id);
    const next = modelIds[i + 1] ?? modelIds[i - 1] ?? null;
    change(
      modelIds.filter((x) => x !== id),
      next,
    );
  };

  return (
    <>
      <BlockNote>
        {typo(
          'По утверждённым ПРЦ этих моделей считается подсказка средней ставки группы. Модель стоит только в одной группе: добавленная из другой группы уйдёт оттуда.',
        )}
      </BlockNote>
      {modelIds.length === 0 && <Empty>В группе пока нет моделей.</Empty>}
      <List aria-label="Модели группы">
        {modelIds.map((id) => {
          const m = info(id);
          const isNew = !before.has(id);
          const from = isNew ? otherGroup(id) : null;
          const name = m?.name ?? 'Модель не найдена в справочнике';
          return (
            <Item
              key={id}
              data-new={isNew ? 'true' : undefined}
              data-moving={from ? 'true' : undefined}
            >
              <div>
                <Name>{name}</Name>
                <Sub>
                  {m?.category && <span>{m.category}</span>}
                  {m && <span>{formatCount(m.units)} ед.</span>}
                  {m && <span>{prcText(m)}</span>}
                  {m && m.status !== 'active' && <FleetStatusChip status={m.status} />}
                  {isNew && !from && <FleetChip $tone="info">добавится</FleetChip>}
                </Sub>
                {from && (
                  <MovingNote>
                    {typo(
                      `Модель уйдёт из группы «${from}» — в группе она может быть только одна.`,
                    )}
                  </MovingNote>
                )}
              </div>
              <Button
                id={removeId(id)}
                type="button"
                $variant="ghost"
                $size="sm"
                aria-label={`Убрать из группы: ${name}`}
                onClick={() => removeModel(id)}
              >
                Убрать
              </Button>
            </Item>
          );
        })}
      </List>
      {removed.length > 0 && (
        <Removed>
          Уберутся из группы и останутся вне групп:{' '}
          {removed.map((id) => {
            const name = info(id)?.name ?? id;
            return (
              <span key={id}>
                {name}
                <TextButton
                  type="button"
                  aria-label={`Вернуть в группу: ${name}`}
                  onClick={() => change([...modelIds, id], id)}
                >
                  вернуть
                </TextButton>
              </span>
            );
          })}
        </Removed>
      )}
      <FieldLabel htmlFor={pickerId}>Добавить модель</FieldLabel>
      <Combobox
        id={pickerId}
        value=""
        options={options}
        onChange={(o) => {
          if (o && !inDraft.has(o.id)) onChange([...modelIds, o.id]);
        }}
        placeholder="Найдите модель по названию или категории"
        emptyText="Такой модели нет или она уже в группе"
        maxItems={40}
      />
      <FieldHint>
        {moving > 0
          ? typo(
              `Перенос из других групп: ${moving}. Он случится при сохранении — до этого обе группы прежние.`,
            )
          : 'Рядом с моделью видно, в какой группе она сейчас.'}
      </FieldHint>
    </>
  );
}
